import React, { FC, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import moment from 'moment';
import translate from '@translate';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import JsonCodeViewer from 'src/components/JsonCodeViewer';
import { TransportApiCallType } from 'src/types/transportDetail';

export interface ApiCallDetailDialogPropsType {
    data: TransportApiCallType;
    onClose: () => void;
}

const stringifyJson = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
};

const MetaRow: FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="body2">{value || '—'}</Typography>
    </Box>
);

const ApiCallDetailDialog: FC<ApiCallDetailDialogPropsType> = (props) => {
    const { data, onClose } = props;

    const requestRaw = useMemo(() => stringifyJson(data.REQUEST), [data.REQUEST]);
    const responseRaw = useMemo(() => stringifyJson(data.RESPONSE), [data.RESPONSE]);
    const errorRaw = useMemo(() => {
        if (!data.ERROR_MESSAGE) {
            return '';
        }
        try {
            const parsed: unknown = JSON.parse(data.ERROR_MESSAGE);
            return stringifyJson(parsed);
        } catch {
            return data.ERROR_MESSAGE;
        }
    }, [data.ERROR_MESSAGE]);

    const calledAt = data.CALLED_AT != null ? moment(data.CALLED_AT).format('L LTS') : '—';

    return (
        <Dialog open maxWidth="lg" fullWidth onClose={onClose} data-testid="transport-api-call-detail-dialog">
            <DialogTitle>
                {translate('transportDetail.apiCalls.detailTitle', {
                    id: String(data.ID),
                    operation: data.OPERATION || '',
                })}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
                        <MetaRow label={translate('transportDetail.apiCalls.table.calledAt')} value={calledAt} />
                        <MetaRow
                            label={translate('transportDetail.apiCalls.table.shipper')}
                            value={data.SHIPPER || ''}
                        />
                        <MetaRow
                            label={translate('transportDetail.apiCalls.table.operation')}
                            value={data.OPERATION || ''}
                        />
                        <MetaRow
                            label={translate('transportDetail.apiCalls.table.httpStatus')}
                            value={data.HTTP_STATUS != null ? String(data.HTTP_STATUS) : ''}
                        />
                        <MetaRow
                            label={translate('transportDetail.apiCalls.table.durationMs')}
                            value={data.DURATION_MS != null ? String(data.DURATION_MS) : ''}
                        />
                    </Stack>

                    {errorRaw ? (
                        <JsonCodeViewer
                            value={errorRaw}
                            label={translate('transportDetail.apiCalls.table.errorMessage')}
                            testId="transport-api-call-detail-error"
                            minHeight={120}
                        />
                    ) : null}

                    <JsonCodeViewer
                        value={requestRaw || '{}'}
                        label={translate('transportDetail.apiCalls.detail.request')}
                        helperText={translate('transportDetail.apiCalls.detail.jsonHint')}
                        testId="transport-api-call-detail-request"
                        minHeight={200}
                    />

                    <JsonCodeViewer
                        value={responseRaw || '{}'}
                        label={translate('transportDetail.apiCalls.detail.response')}
                        helperText={translate('transportDetail.apiCalls.detail.jsonHint')}
                        testId="transport-api-call-detail-response"
                        minHeight={200}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-testid="transport-api-call-detail-close">
                    {translate('confirmDialog.cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ApiCallDetailDialog;
